import { z } from 'zod';
export const localeSchema = z.enum(['zh-CN', 'en-US']);
export const publishScopeSchema = z.enum(['zh', 'en', 'both']);
export function publishScopeAllows(scope, locale) {
    return scope === 'both' || scope === (locale === 'zh-CN' ? 'zh' : 'en');
}
export function publishScopeLocales(scope) {
    if (scope === 'zh')
        return ['zh-CN'];
    if (scope === 'en')
        return ['en-US'];
    return ['zh-CN', 'en-US'];
}
export const productStatusSchema = z.enum(['draft', 'published', 'archived']);
export const productTranslationInputSchema = z.object({
    locale: localeSchema,
    name: z.string().trim().min(1).max(160),
    summary: z.string().trim().max(500).nullable().optional(),
    descriptionHtml: z.string().max(100_000).nullable().optional(),
    features: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
    applications: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
    seoTitle: z.string().trim().max(160).nullable().optional(),
    seoKeywords: z.string().trim().max(500).nullable().optional(),
    seoDescription: z.string().trim().max(320).nullable().optional(),
    brand: z.string().trim().max(160).nullable().optional(),
    certification: z.string().trim().max(240).nullable().optional(),
    origin: z.string().trim().max(160).nullable().optional(),
    customAttributes: z.array(z.object({
        name: z.string().trim().min(1).max(120),
        value: z.string().trim().min(1).max(500),
        sortOrder: z.number().int().min(0).max(100_000).default(0)
    })).max(50).default([])
});
export const productSpecInputSchema = z.object({
    locale: localeSchema,
    groupName: z.string().trim().max(120).nullable().optional(),
    label: z.string().trim().min(1).max(160),
    value: z.string().trim().min(1).max(1_000),
    sortOrder: z.number().int().min(0).max(100_000).default(0)
});
const productMediaInputSchema = z.object({
    mediaId: z.number().int().positive(),
    role: z.string().trim().min(1).max(32).default('gallery'),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    altZh: z.string().trim().max(240).nullable().optional(),
    altEn: z.string().trim().max(240).nullable().optional()
});
const productAttachmentInputSchema = z.object({
    mediaId: z.number().int().positive(),
    locale: localeSchema.nullable().optional(),
    title: z.string().trim().min(1).max(240),
    sortOrder: z.number().int().min(0).max(100_000).default(0)
});
export const productInputSchema = z.object({
    slug: z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(160),
    sku: z.string().trim().max(80).nullable().optional(),
    categoryId: z.number().int().positive(),
    status: productStatusSchema.default('draft'),
    publishScope: publishScopeSchema.default('both'),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    isFeatured: z.boolean().default(false),
    moq: z.string().trim().max(160).nullable().optional(),
    price: z.string().trim().max(320).nullable().optional(),
    paymentMethod: z.string().trim().max(320).nullable().optional(),
    supplyAbility: z.string().trim().max(320).nullable().optional(),
    deliveryTime: z.string().trim().max(160).nullable().optional(),
    packaging: z.string().trim().max(500).nullable().optional(),
    translations: z.array(productTranslationInputSchema).min(1).max(2)
        .refine((items) => new Set(items.map((item) => item.locale)).size === items.length, {
        message: '同一语言只能提交一次'
    }),
    specs: z.array(productSpecInputSchema).max(200).default([]),
    media: z.array(productMediaInputSchema).max(200).default([]),
    attachments: z.array(productAttachmentInputSchema).max(200).default([]),
    videoIds: z.array(z.number().int().positive()).max(50).default([])
}).superRefine((input, context) => {
    if (input.status !== 'published')
        return;
    const submitted = new Set(input.translations.map((item) => item.locale));
    for (const locale of publishScopeLocales(input.publishScope)) {
        if (submitted.has(locale))
            continue;
        context.addIssue({
            code: 'custom',
            path: ['translations'],
            message: `发布到${locale === 'zh-CN' ? '中文' : '英文'}网站前，请先填写对应语言内容`
        });
    }
});
export const loginInputSchema = z.object({
    username: z.string().trim().min(1).max(80),
    password: z.string().min(8).max(256)
});
export const inquiryInputSchema = z.object({
    productId: z.number().int().positive().nullable().optional(),
    name: z.string().trim().min(1).max(120),
    company: z.string().trim().max(160).nullable().optional(),
    email: z.string().trim().email().max(254),
    phone: z.string().trim().max(80).nullable().optional(),
    country: z.string().trim().max(120).nullable().optional(),
    message: z.string().trim().min(10).max(5_000),
    locale: localeSchema.default('en-US'),
    sourcePath: z.string().trim().max(500).nullable().optional(),
    website: z.string().max(0).optional()
});
export function localeFromHost(host, requested) {
    const parsed = localeSchema.safeParse(requested);
    if (parsed.success)
        return parsed.data;
    return host?.toLowerCase().includes('jws.com.cn') ? 'zh-CN' : 'en-US';
}
//# sourceMappingURL=schemas.js.map